import { IGenericAccessor } from '@src/suite/common/interfaces/i-generic-accessor';
import { v4 } from 'uuid';

import { MosObjectType, parseMosMsg } from './mos-utlis';
import { formatTime } from './timing-format';
import { findFirstNodeByTagName, parseXml } from './xml-utils';

export enum ProductionCommandType {
    TAKE = 'TAKE',
    TECH = 'TECH',
    INSERT = 'INSERT',
    ANCHOR = 'ANCHOR',
    READRATE = 'READRATE',
    DURATION = 'DURATION',
    NOTES = 'NOTES',
    AUTOMATION_CLIP = 'AUTOMATION:CLIP',
    AUTOMATION_STILL = 'AUTOMATION:STILL',
    AUTOMATION_CG = 'AUTOMATION:CG',
    AUTOMATION_INSET = 'AUTOMATION:INSET',
    AUTOMATION_CAMERA = 'AUTOMATION:CAMERA',
    AUTOMATION_LIVEEVENT = 'AUTOMATION:LIVE',
    AUTOMATION_OS = 'AUTOMATION:OS',
    AUTOMATION_LOGO = 'AUTOMATION:LOGO',
    AUTOMATION_MACRO = 'AUTOMATION:MACRO',
    UNKNOWN = 'UNKNOWN',
}

export enum ProductionFieldName {
    GUID = 'guid',
    COMMAND_TYPE = 'commandType',
    HEADER_CONTENT = 'headerContent',
    TEXT = 'TEXT',
    TAKE = 'TAKE',
    INSERT = 'INSERT',
    ANCHOR = 'ANCHOR',
    READRATE = 'READRATE',
    NOTES = 'NOTES',
    DURATION = 'DURATION',
    ITEM_TIME = 'ITEM TIME',
    OPTION = 'OPTION',
    OPTION_DESCRIPTION = 'OPTION_DESCRIPTION',
    NAME = 'NAME',
    NUMBER = 'NUMBER',
    INCUE = 'INCUE',
    OUTCUE = 'OUTCUE',
    AT = 'AT',
    TO = 'TO',
    TIME_FIELD = 'timeField',
    TIME_VALUE = 'timeValue',
}

export const ProductionCommandsTitle = {
    [ProductionCommandType.TAKE]: 'Take',
    [ProductionCommandType.INSERT]: 'Insert',
    [ProductionCommandType.ANCHOR]: 'Anchor',
    [ProductionCommandType.READRATE]: 'Read Rate',
    [ProductionCommandType.DURATION]: 'Duration',
    [ProductionCommandType.NOTES]: 'Notes',
};

export const ProductionCommandsDefaultLabels = {
    [ProductionFieldName.OPTION]: 'Select Object',
    [ProductionFieldName.TAKE]: 'Take',
    [ProductionFieldName.INSERT]: 'Insert',
    [ProductionFieldName.ANCHOR]: 'Enter Name',
    [ProductionFieldName.NAME]: 'Name',
    [ProductionFieldName.NUMBER]: 'Enter Number',
    [ProductionFieldName.INCUE]: 'Enter InCue',
    [ProductionFieldName.OUTCUE]: 'Enter OutCue',
    [ProductionFieldName.AT]: 'At',
    [ProductionFieldName.TO]: 'To',
    [ProductionFieldName.READRATE]: 'Enter Read Rate',
    [ProductionFieldName.NOTES]: 'Enter Notes',
    [ProductionFieldName.DURATION]: 'Duration',
    [ProductionFieldName.ITEM_TIME]: 'Item Time',
};

export const ProductionCommandsPlaceholderLabels = {
    [ProductionFieldName.AT]: 'At 00\'00"',
    [ProductionFieldName.TO]: 'To 00\'00"',
    [ProductionFieldName.DURATION]: '00:00:00',
    [ProductionFieldName.ITEM_TIME]: '00:00:00',
};

export const ProductionCommandsObjectOptions: {
    id: string;
    title: string;
}[] = [
    { id: 'VO', title: 'VO' },
    { id: 'SOT', title: 'SOT' },
    { id: 'PKG', title: 'PKG' },
    { id: 'Video VO', title: 'Video VO' },
    { id: 'FS', title: 'FS' },
    { id: 'OTS', title: 'OTS' },
    { id: 'Live', title: 'Live' },
    { id: 'Studio', title: 'Studio' },
    { id: 'Last Shot Change', title: 'Last Shot Change' },
    { id: 'Wipe', title: 'Wipe' },
    { id: 'Mix', title: 'Mix' },
    { id: 'Freeze', title: 'Freeze' },
    { id: 'Insert', title: 'Insert' },
    { id: 'Change Insert', title: 'Change Insert' },
    { id: 'Sound Upcoming', title: 'Sound Upcoming' },
    { id: 'Sound Up', title: 'Sound Up' },
    { id: 'Dup and Cue', title: 'Dup and Cue' },
    { id: 'Other', title: 'Other' },
];

export const blackLineRegex = /<span[^>]*id="BLACKLINE"[^>]*>(.*?)<\/span>/g;
export const blackLineSpan = '<span id="BLACKLINE"></span>';

const commandTypeRegex = new RegExp(`^(${Object.values(ProductionCommandType).join('|')})`, 'i');

export class ML2HtmlUtils {
    public static fixURLs(text) {
        // Detects http, https and ftp links without the appropriate markup
        //                     tag     inner
        return text.replace(
            // Match all text between tags
            /<([^>]+)>([^<]+)/gm,
            ($0, tag, inner) => {
                if (tag.substr(0, 1).toLowerCase() === 'a' && tag.toLowerCase().match(/class="external-link"/) && tag.toLowerCase().match(/target="_blank"/)) {
                    // If we have an <a> tag with class="external-link" and target="_blank" attached the URL is already processed
                    return $0;
                }
                inner = inner.replace(
                    // Match URLs and replace them
                    /^((http|https|ftp)\:\/\/)[a-z0-9\-]+\.[a-z]{2,4}(:[a-z0-9]*)?\/?([a-z0-9\-\._\?\,\'\/\\\+&amp;%\$#\=~])*/gim,
                    ($1, protocol) => {
                        let _path = '';
                        if (!protocol) {
                            _path = `http://${$1}`;
                        } else {
                            _path = $1;
                        }
                        return `<a href="${_path}" style="text-decoration:underline;background-color:#FFFFFF;color:#5F9EA0;cursor:pointer" class="external-link" target="_blank">${_path}</a>`;
                    }
                );
                return `<${tag}>${inner}`;
            }
        );
    }

    public static fixHTMLOverBL(text) {
        text = text.replace(new RegExp('(</title>)( ?&nbsp;)*(<div[^>]id="BLACKLINE"[^>]*?>)', 'gi'), (match, p1, p2, p3) => p1 + p3);
        text = text.replace(new RegExp('(</title>>)(<span[^>]*>[s]*[^\n]</span>|[s]*)(<div[^>]id="BLACKLINE"[^>]*?>)', 'gi'), (match, p1, p2, p3) => p1 + p3);
        text = text.replace(new RegExp('(</title>>)(<span[^>]*>( ?&nbsp;)*</span>|[s]*)(<div[^>]id="BLACKLINE"[^>]*?>)', 'gi'), (match, p1, p2, p3, p4) => p1 + p4);
        text = text.replace(new RegExp('(</title>)(<div[^>]id="BLACKLINE"[^>]*?>)', 'gi'), (match, p1, p2) => `${p1}<wbr contenteditable="false"><span>&nbsp;</span>${p2}`);
        text = text.replace(new RegExp('(.*)(<title>[^]*</title>)(.*)', 'gi'), (match, p1, p2, p3) => p2 + p1 + p3);
        return text;
    }

    // converts ML2 format from current record and returns html for display
    // the main work here is for the textcommands
    public static convertML2ToDisplayHtml(textVal: string, rec: IGenericAccessor) {
        let text = textVal;

        if (!text) text += blackLineSpan;

        if (text !== '') {
            // get's called a lot as an empty string like for every item in the list...should speed things up a bit by not trying to process an empty string!
            text = text
                .replace(/~C13~~C10~/g, '<br/>')
                .replace(/~C10~/g, '<br/>')
                .replace(/~C9~/g, '&nbsp;')
                .replace(/~C13~/g, '')
                .replace(/[\u00A0]/g, ' ');

            text = text.replace(new RegExp('\t', 'g'), '&emsp;');
            // handle &#160; conversion to &nbsp;
            text = text.replace(new RegExp('&#160;&#160;', 'g'), ' &nbsp;');
            text = text.replace(new RegExp('&#160;', 'g'), ' ');

            if (text.indexOf('<body') !== -1) {
                // extract the body
                const bodyMatches = text.match(/<body>(.*?)<\/body>/);
                if (bodyMatches && bodyMatches.length > 0) {
                    text = bodyMatches[1]?.trim();
                }
            }

            // fix URLs
            text = text.replace(/<span xml:lang="en-us" style="([^"]*)">([^<]*)<\/span>/g, ($0, style, url) => {
                if (style.match(/text-decoration:\s*underline/) && style.match(/color:\s*#5F9EA0/)) {
                    return `<a href="${url}" style="text-decoration:underline;background-color:#FFFFFF;color:#5F9EA0;cursor:pointer" class="external-link" target="_blank">${url}</a>`;
                }
                return $0;
            });
            text = ML2HtmlUtils.fixURLs(text);

            text = ML2HtmlUtils._stripDefaultColorStyles(text);

            const hasBlackLine = blackLineRegex.test(text);
            if (!hasBlackLine) {
                const parsedText = parseXml(text, 'text/html');
                const body = parsedText.getElementsByTagName('body')[0];
                if (body) {
                    body.innerHTML += blackLineSpan;
                    text = body.innerHTML;
                } else {
                    console.error('!IMPORTANT: there is no body, should never happen, but if we see this message, something is wrong and we need to fix it');
                }
            }

            const textCommands = rec.getRawValue('TextCommands');

            // handle text commands
            if (textCommands && textCommands !== null && Object.keys(textCommands).length !== 0) {
                const parsedText = parseXml(text, 'text/html');
                if (parsedText) {
                    for (const command in textCommands) {
                        const guid = command;
                        const spanElement = parsedText.getElementById(guid);
                        if (!spanElement) continue;
                        const commandText = textCommands[command].replace(new RegExp('\r\n|\n', 'g'), '<br/>');
                        const isProduction = spanElement.classList.contains('PRODUCTIONCOMMAND');
                        let commandXml: Element;
                        if (isProduction) {
                            commandXml = this.generateProductionCommandHtml(commandText, guid);
                        } else {
                            commandXml = this.generateMosItemHtml(commandText, guid);
                        }
                        if (!commandXml) continue;
                        const spanParent = spanElement.parentElement;
                        spanParent?.replaceChild(commandXml, spanElement);
                        // spanParent?.insertBefore(document.createElement('p'), commandXml);
                    }
                    // ensure and empty paragraph between production commands
                    const childElements = parsedText.getElementsByTagName('body')?.[0]?.children;
                    if (childElements && childElements.length > 0) {
                        // iterate backwards because we are inserting after
                        const length = childElements.length;
                        for (let i = length - 1; i >= 0; i--) {
                            const el = childElements[i];
                            const isProduction = el.tagName?.toLowerCase() === 'div';
                            const isFirst = i === 0;
                            const isLast = i === length - 1;
                            if (isLast) {
                                if (isProduction) {
                                    el.insertAdjacentElement('afterend', document.createElement('p'));
                                }
                            }
                            if (isFirst) {
                                if (isProduction) {
                                    el.insertAdjacentElement('beforebegin', document.createElement('p'));
                                }
                            } else {
                                if (isProduction) {
                                    const prevEl = childElements[i - 1];
                                    if (prevEl.tagName?.toLowerCase() === 'div') {
                                        el.insertAdjacentElement('beforebegin', document.createElement('p'));
                                    }
                                }
                            }
                        }
                    }
                    text = parsedText.documentElement.outerHTML;
                } else {
                    console.log('parsedText is null');
                }
            }

            // ensure an empty row if there is no content above the black line, so a user can enter content
            text = text.replace(new RegExp('(<body>)(<span[^>]*>[s]*[^\n]</span>|[s]*)(<div[^>]id="BLACKLINE"[^>]*?>)', 'gi'), (match, p1, p2, p3) => p1 + p3);
            text = text.replace(new RegExp('(<body>)(<span[^>]*>( ?&nbsp;)*</span>|[s]*)(<div[^>]id="BLACKLINE"[^>]*?>)', 'gi'), (match, p1, p2, p3, p4) => p1 + p4);
            text = text.replace(new RegExp('(<body>)(<div[^>]id="BLACKLINE"[^>]*?>)', 'gi'), (match, p1, p2) => `${p1}<wbr contenteditable="false"><span>&nbsp;</span>${p2}`);
        }
        // Not fully sure for this one
        // text = text.replace(/<body>|<\/body>/g, '');
        if (text === '<br>') {
            text = '';
        }

        const bodyMatches = text?.match(/<body>(.*?)<\/body>/);
        const headMatches = text?.match(/<head>(.*?)<\/head>/);
        const htmlMatches = text?.match(/<html>(.*?)<\/html>/);

        if (bodyMatches && bodyMatches.length > 0) {
            text = bodyMatches[1]?.trim();
        } else if (headMatches && headMatches.length > 0) {
            text = text?.replace(headMatches[0], '')?.trim();
        } else if (htmlMatches && htmlMatches.length > 0) {
            text = htmlMatches[1]?.trim();
        }

        // I don't think it will ever enter here because of the replace above (text = text.replace(/<body>|<\/body>/g, ''))
        // if (text.endsWith('</div></body></html>') || text.endsWith('</div></body>') || text.endsWith('</div>')) {
        //     const parsedText = parseXml(text, 'text/html');
        //     const newLine = parsedText.createElement('p');
        //     parsedText.body.appendChild(newLine);
        //     text = parsedText.documentElement.outerHTML;
        // }
        if (text.endsWith('</div>')) {
            const parsedText = parseXml(text, 'text/html');
            const newLine = parsedText.createElement('p');
            parsedText.body.appendChild(newLine);
            text = parsedText.getElementsByTagName('body')?.[0]?.innerHTML;
        }

        if (text.includes('</body>')) {
            const parsedText = parseXml(text, 'text/html');
            text = parsedText.getElementsByTagName('body')?.[0]?.innerHTML;
        }
        return text;
    }

    /**
     * Given a text command, returns an HTML element that can be inserted into the story as a production command.
     * If the commandText starts with '<mos>', it will be interpreted as a mos item and converted to an HTML element using generateMosItemHtml.
     * Otherwise, it will be interpreted as a production command and converted to an HTML element using generateProductionCommandHtml.
     * @param commandText The text command to convert to an HTML element.
     * @param guid The guid of the element to generate.
     * @returns An HTML element that can be inserted into the story as a production command.
     */
    private static generateTextCommandHtml(commandText: string, guid: string): Element {
        if (!commandText) return null;
        if (!guid) return null;
        if (commandText.endsWith(']')) commandText = commandText.substring(1, commandText.length - 1);
        if (commandText.startsWith('[')) commandText = commandText.substring(1);

        if (commandText.startsWith('<mos>')) {
            return this.generateMosItemHtml(commandText, guid);
        } else {
            return this.generateProductionCommandHtml(commandText, guid);
        }
    }

    public static generateProductionCommandHtml(commandText: string, guid: string): Element {
        if (!commandText) return null;
        if (!guid) return null;
        if (commandText.endsWith(']')) commandText = commandText.substring(1, commandText.length - 1);
        if (commandText.startsWith('[')) commandText = commandText.substring(1);

        const productionCommandText = commandText;

        const productionCommandType = this.extractProductionCommandType(productionCommandText);
        const productionCommandContentArray = productionCommandText?.split('<br/>');
        let productionCommandTitle = productionCommandText;
        let productionCommandDetailsDiv = '';

        if (productionCommandContentArray && productionCommandContentArray.length > 0) {
            const commandTypeOption = productionCommandContentArray[0].replace(commandTypeRegex, '').trim();
            const commandTypeOptionStart = commandTypeOption.startsWith(':') ? commandTypeOption.substring(1) : commandTypeOption;
            productionCommandTitle = productionCommandType + (productionCommandType !== ProductionCommandType.NOTES ? ` - ` + commandTypeOptionStart : '');
            productionCommandDetailsDiv =
                productionCommandContentArray.length > 1
                    ? `<div class="st-rich-text-editor--production-details">` +
                      productionCommandContentArray
                          .slice(1)
                          .map(item => {
                              const parts = item.split(':');
                              if (parts.length >= 2) {
                                  return `<div class="st-rich-text-editor--production-details--command">${
                                      parts[0]
                                  }:</div><div class="st-rich-text-editor--production-details--command-text">${item.replace(parts[0] + ':', '')}</div>`;
                              }
                              return `<div class="st-rich-text-editor--production-details--command"></div><div class="st-rich-text-editor--production-details--command-text"></div>`;
                          })
                          .join('') +
                      `</div>`
                    : productionCommandType === ProductionCommandType.NOTES
                      ? `<div class="st-rich-text-editor--production-details--notes"><div class="st-rich-text-editor--production-details--command-text">${commandTypeOptionStart}</div></div>`
                      : '';
        }

        const productionCommandDivString = `
        <div contenteditable="false" draggable="true" class="st-rich-text-editor--production" id="${guid}">
            <div class="st-rich-text-editor--production-transparent">[PROD]</div>
            <div class="st-rich-text-editor--production-header">
                <div class="st-rich-text-editor--production-header--left">
                        <span class="material-symbols-outlined">drag_indicator</span>
                    <span class="material-symbols-outlined">${this.getCommandTypeIcon(productionCommandType)}</span>
                    <span class="st-rich-text-editor--production-header--title">${productionCommandTitle}</span>
                </div>
                <div class="st-rich-text-editor--production-header--right">
                    ${
                        productionCommandContentArray.length > 1 || productionCommandType === ProductionCommandType.NOTES
                            ? '<span class="st-rich-text-editor--production-expander material-symbols-outlined">expand_less</span>'
                            : ''
                    }
                    ${this.isProductionCommandEditable(productionCommandType) ? '<span class="st-rich-text-editor--settings material-symbols-outlined">settings</span>' : ''}

                    <span class="st-rich-text-editor--production-close material-symbols-outlined">close</span>
                </div>
            </div>
            ${productionCommandDetailsDiv}
            <div class="st-rich-text-editor--production-transparent">[/PROD]</div>
        </div>`;
        return findFirstNodeByTagName(productionCommandDivString, 'div');
    }

    public static generateMosItemHtml(mosItem: string, guid: string): Element {
        if (!mosItem) return null;
        if (!guid) return null;
        if (mosItem.endsWith(']')) mosItem = mosItem.substring(1, mosItem.length - 1);
        if (mosItem.startsWith('[')) mosItem = mosItem.substring(1);
        const mosItemXml = parseMosMsg(mosItem);
        if (!mosItemXml) return null;

        const objType = (mosItemXml.getElementsByTagName('objType')?.item(0)?.textContent?.toUpperCase() as MosObjectType) || MosObjectType.SUMMARY;
        const proxyPaths = mosItemXml.getElementsByTagName('objProxyPath');
        let thumbnailUrl = null;
        if (proxyPaths?.length > 0) {
            for (let i = 0; i < proxyPaths.length; i++) {
                const url = proxyPaths[i].textContent;
                if (!url?.length) continue;

                thumbnailUrl = url;
                //TODO: I think this is not needed. We can just use the proxy path as is
                /*
                const urlChunks = url.split('.');
                const extension = urlChunks[urlChunks.length - 1].toLowerCase();
                if (extension === 'jpg' || extension === 'jpeg' || extension === 'png' || extension === 'gif' || extension === 'bmp' || extension === 'tiff') {
                    if (url.toLowerCase().indexOf('http://') === 0) {
                        thumbnailUrl = url;
                    } else if (url.toLowerCase().indexOf('https://') === 0) {
                        thumbnailUrl = url;
                    } else {
                        thumbnailUrl = `http://${url}`;
                    }
                    break;
                }
                */
            }
        }

        const abstractText = mosItemXml.getElementsByTagName('mosAbstract')?.item(0)?.textContent;
        let slugText = null;
        let itemDuration = '';
        if (!abstractText?.length) {
            slugText = mosItemXml.getElementsByTagName('objSlug')?.item(0)?.textContent;
            if (!slugText) slugText = mosItemXml.getElementsByTagName('itemSlug')?.item(0)?.textContent;

            const itemDur = mosItemXml.getElementsByTagName('objDur')?.item(0)?.textContent;
            const timebase = mosItemXml.getElementsByTagName('objTB')?.item(0)?.textContent;
            if (itemDur && timebase) {
                const itemDurNum = Number(itemDur);
                const timebaseNum = Number(timebase);
                itemDuration = formatTime(itemDurNum / timebaseNum);
            }
            // eslint-disable-next-line eqeqeq
            slugText = slugText?.length && itemDuration?.length ? `${slugText} ${itemDuration}` : slugText?.length ? slugText : '';
        } else {
            slugText = abstractText;
        }
        const mosId = mosItemXml.getElementsByTagName('mosID')?.item(0)?.textContent;

        // uncoment this to test tumbnail
        // thumbnailUrl = 'https://w7.pngwing.com/pngs/41/829/png-transparent-spongebob-running-cartoons-spongebob-thumbnail.png';

        const mosDivXml = findFirstNodeByTagName(
            `<div contenteditable="false" draggable="true" class="st-rich-text-editor--mos st-rich-text-editor--mos--${(objType || MosObjectType.SUMMARY)?.toLowerCase()}" ${
                mosId ? `mosclass="-${mosId}" mosid="${mosId}"` : ''
            } id="${guid}">
            <div class="st-rich-text-editor--mos-transparent">[MOS]</div>
                <div class="st-rich-text-editor--mos-header">
                    <div class="st-rich-text-editor--mos-header--left">
                        <span class="material-symbols-outlined">drag_indicator</span>
                        <span class="material-symbols-outlined">${this.getMosTypeIcon(objType)}</span>
                        <span class="st-rich-text-editor--mos-info--title">${slugText}</span>
                    </div>
                    <div class="st-rich-text-editor--mos-header--right">
                        ${thumbnailUrl ? '<span class="st-rich-text-editor--production-expander material-symbols-outlined">expand_more</span>' : ''}
                        <span class="st-rich-text-editor--mos-open-external material-symbols-outlined">open_in_new</span>
                        <span class="st-rich-text-editor--production-close material-symbols-outlined">close</span>
                    </div>
                </div>
                ${
                    thumbnailUrl
                        ? `<div class="st-rich-text-editor--mos-details st-rich-text-editor--mos-details--hidden">
                        <img class="st-rich-text-editor--mos-details--tumb" src="${thumbnailUrl}" />
                    </div>`
                        : ''
                }
                <div class="st-rich-text-editor--mos-transparent">[/MOS]</div>
            </div>`,
            'div'
        );
        return mosDivXml;
    }

    private static getMosTypeIcon(mosType: MosObjectType): string {
        switch (mosType) {
            case MosObjectType.STILL:
                return 'image';
            case MosObjectType.VIDEO:
                return 'videocam';
            case MosObjectType.AUDIO:
                return 'mic';
            default:
                return 'data_object';
        }
    }

    public static isProductionCommandEditable(commandType: string): boolean {
        switch (commandType) {
            case ProductionCommandType.TAKE:
            case ProductionCommandType.INSERT:
            case ProductionCommandType.ANCHOR:
            case ProductionCommandType.READRATE:
            case ProductionCommandType.DURATION:
            case ProductionCommandType.NOTES:
                return true;
            default:
                return false;
        }
    }

    public static getCommandTypeIcon(commandType: string): string {
        switch (commandType) {
            case ProductionCommandType.TAKE:
                return 'movie';
            case ProductionCommandType.TECH:
                return 'construction';
            case ProductionCommandType.INSERT:
                return 'precision_manufacturing';
            case ProductionCommandType.ANCHOR:
                return 'co_present';
            case ProductionCommandType.READRATE:
                return 'record_voice_over';
            case ProductionCommandType.DURATION:
                return 'av_timer';
            case ProductionCommandType.NOTES:
                return 'notes';
            default:
                return 'autoplay';
        }
    }

    private static extractProductionCommandType(commandText: string): ProductionCommandType {
        if (!commandText) return ProductionCommandType.UNKNOWN;

        const trimmedCommandText = commandText.trim().toUpperCase();
        const commandTypeMatch = trimmedCommandText.match(commandTypeRegex);
        if (commandTypeMatch) {
            return commandTypeMatch[0] as ProductionCommandType;
        }
    }

    public static convertDisplayHTMLToML2(text) {
        // remove the trailing span (if any)
        text = text
            .replace(/~C13~~C10~/g, '<br/>')
            .replace(/~C10~/g, '<br/>')
            .replace(/~C9~/g, '&nbsp;')
            .replace(/~C13~/g, '')
            .replace(/[\u00A0]/g, ' ');
        text = text.replace(new RegExp('</title><wbr contenteditable="false">', 'g'), '</title>');

        // remove the beginning/trailing span (if any)
        text = text.replace(new RegExp('<span>&nbsp;</span>', 'g'), '');
        // fix URLs
        text = ML2HtmlUtils.fixURLs(text);
        //TODO I am not sure about this text replace should talk with Yanko about it
        // text = text.replace(/<a[^>]*target="_blank"[^>]*>([^<]*)<\/a>/g, '<span xml:lang="en-us" style="text-decoration:underline;background-color:#FFFFFF;color:#5F9EA0;">$1</span>');

        // spacing, font styles and other general fixes to markup
        text = text.replace(new RegExp('&emsp;', 'g'), '&#9;');
        text = text.replace(new RegExp(' &nbsp;', 'g'), '&#160;&#160;');
        text = text.replace(new RegExp('&nbsp;', 'g'), '&#160;');
        text = ML2HtmlUtils._stripDefaultColorStyles(text);

        text = text.replace(new RegExp('xml: lang = "en-us"', 'g'), '');
        text = text.replace(new RegExp('<br>', 'g'), '<br/>');

        text = ML2HtmlUtils.removeDivById(text, 'aboveBlackLine');
        text = ML2HtmlUtils.removeDivById(text, 'belowBlackLine');

        // remove black line protection
        // text = text.replace(new RegExp('<div id="BLACKLINE"(.*?)/div>', 'g'), '<span id="BLACKLINE"></span>');

        // return MIR/PC blocks to spans and remove inner content
        text = text.replace(/<wbr \/>/g, '');
        text = text.replace(/<wbr>/g, '');
        // if (ML2HtmlUtils.isIOSDevice()) {
        //     text = text.replace(/class=\"st-rich-text-editor--production iOSDevice\"/g, 'class="PRODUCTIONCOMMAND iOSDevice"');
        // }

        const parsedText = parseXml(`<html xmlns="http://www.w3.org/1999/xhtml"><body>${text}</body></html>`, 'text/html');
        const productionCommandElements = parsedText.getElementsByClassName('st-rich-text-editor--production');
        const productionCommandElementsSpanElements = [];
        for (let i = 0; i < productionCommandElements.length; i++) {
            const commandElement = productionCommandElements.item(i);
            const id = commandElement.getAttribute('id');
            const commandSpan = parsedText.createElement('span');
            commandSpan.setAttribute('class', 'PRODUCTIONCOMMAND');
            commandSpan.setAttribute('id', id);
            productionCommandElementsSpanElements.push(commandSpan);
        }
        productionCommandElementsSpanElements.forEach(span => {
            const commandDiv = parsedText.getElementById(span.getAttribute('id'));
            try {
                commandDiv.parentNode.replaceChild(span, commandDiv);
            } catch (error) {
                console.log('Error replacing MOS div with span', error);
            }
        });
        const mosElements = parsedText.getElementsByClassName('st-rich-text-editor--mos');
        const mosSpanElements = [];
        for (let i = 0; i < mosElements.length; i++) {
            const mosElement = mosElements.item(i);
            const mosClass = mosElement.getAttribute('mosclass');
            const id = mosElement.getAttribute('id');
            const mosSpan = parsedText.createElement('span');
            mosSpan.setAttribute('class', `MOS${mosClass}`);
            mosSpan.setAttribute('id', id);
            mosSpanElements.push(mosSpan);
        }
        mosSpanElements.forEach(mosSpan => {
            const mosDiv = parsedText.getElementById(mosSpan.getAttribute('id'));
            try {
                mosDiv.parentNode.replaceChild(mosSpan, mosDiv);
            } catch (error) {
                console.log('Error replacing MOS div with span', error);
            }
        });
        // extract content from paragraphs
        const paragraphs = parsedText.getElementsByTagName('p');
        for (let i = paragraphs.length - 1; i >= 0; i--) {
            const paragraph = paragraphs.item(i);
            const parent = paragraph.parentNode;

            let endsWithBr = false;
            while (paragraph.firstChild) {
                // check if it is a text node, then create a span and append the text node to it
                endsWithBr = paragraph.firstChild.nodeName.toLowerCase() === 'br';
                if (paragraph.firstChild.nodeType === 3) {
                    const span = parsedText.createElement('span');
                    span.appendChild(paragraph.firstChild);
                    parent.insertBefore(span, paragraph);
                } else {
                    parent.insertBefore(paragraph.firstChild, paragraph);
                }
            }
            if (!endsWithBr) {
                parent.insertBefore(parsedText.createElement('br'), paragraph);
            }
            parent.removeChild(paragraph);
        }
        // TODO: doesn't make sense to add a <br> at the end of the string, when the next step we will remove all br tags at the end of the story
        // if (parsedText.body.lastChild !== null && parsedText.body.lastChild.nodeName.toLowerCase() !== 'br') {
        //     parsedText.body.appendChild(parsedText.createElement('br'));
        // }

        // iterate body from lasto to first and remove all br tags at the end of the string
        let lastChild = parsedText.body.lastChild;
        while (lastChild !== null && lastChild.nodeName.toLowerCase() === 'br') {
            parsedText.body.removeChild(lastChild);
            lastChild = parsedText.body.lastChild;
        }

        text = parsedText.body.innerHTML;
        // remove all  xmlns="http://www.w3.org/1999/xhtml" from the html
        text = text.replace(new RegExp(' xmlns="http://www.w3.org/1999/xhtml"', 'g'), '');
        text = text.replace(new RegExp(' >', 'g'), '>');
        // replace all the br containing multiple spaces tags with <br/>
        text = text.replace(new RegExp('<br\\s*>', 'g'), '<br/>');

        // text = text.replace(new RegExp("<div [^>]*class=\"PRODUCTIONCOMMAND\"[^>]*>[^>]*</div>", "g"), function (match) {
        /*
        text = text.replace(/class=\"st-rich-text-editor--production[^\"]\"/g, 'class="PRODUCTIONCOMMAND"');
        text = text.replace(new RegExp('<div [^>]*class="PRODUCTIONCOMMAND"[^>]*>.*?</div>', 'g'), match => {
            const id = match.match('id="[^"]*"')[0];
            return `<span class="st-rich-text-editor--production" ${id}></span>`;
        });
        */
        /*
        text = text.replace(new RegExp('<div [^>]*class="st-rich-text-editor--MOS[^"]*"[^>]*>.*?class="st-rich-text-editor--mos-details".*?</div>.*?</div>', 'g'), match => {
            const id = match.match('id="[^"]*"')[0];
            // let mosclass = match.match("mosclass=\"[^\"]+\"")[0];
            const mosclass = match.match('mosclass="[^"]*"')?.[0];
            return `<span class="MOS" ${mosclass} ${id}></span>`;
        });

        text = text.replace(new RegExp('oncontrolselect="return false;" contenteditable="false"', 'g'), '');
        text = text.replace(new RegExp('contenteditable="false" oncontrolselect="return false;"', 'g'), '');
        text = text.replace(new RegExp(' class="st-rich-text-editor--MOS.*?"', 'g'), '');
        text = text.replace(new RegExp('mosclass="', 'g'), 'class="MOS');
        text = text.replace(new RegExp('<title>.*?</title>', ''), '');
        */

        // IE and Mozilla fix convert rgb(val, val, val) to HEX
        text = text.replace(
            /rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/g,
            ($0, $1, $2, $3) => `#${`0${Number($1).toString(16)}`.substr(-2)}${`0${Number($2).toString(16)}`.substr(-2)}${`0${Number($3).toString(16)}`.substr(-2)}`
        );

        if (text.indexOf('<html') !== -1) {
            // trim off anything after closing body tag
            text = text.substring(0, text.indexOf('</body>') + 7);
            text = text.substring(`</head>${7}`);
        }

        // if it is a newly created empty story to remove the inserted <br/> tag so the web services to recognise it is empty
        if (text === '<br/>') {
            text = '';
        }

        // if there is a <title>something</title> remove it
        const titleMatch = text.match(/<title>(.*?)<\/title>/);
        if (titleMatch) {
            text = text.replace(titleMatch[0], '');
        }

        if (text.indexOf('<body') === -1) {
            text = `<body>${text}</body>`;
        }
        // text = text.replace(new RegExp('xml:lang="en-us"', 'g'), '');
        const finalHtml = parseXml(text, 'text/html');
        return `<body>${finalHtml.body.innerHTML}</body>`;
        return text;
        // convert production commands back to original state
    }

    private static _stripDefaultColorStyles(text) {
        text = text.replace(new RegExp('color: rgb\\(0, 0, 0\\);', 'g'), '');
        text = text.replace(new RegExp('color: rgb\\(255, 255, 255\\);', 'g'), '');
        text = text.replace(new RegExp('background-color:#FFFFFF;', 'g'), '');
        text = text.replace(new RegExp('color:#000000;', 'g'), '');
        text = text.replace(new RegExp('style=""', 'g'), '');
        return text;
    }

    private static removeDivById(input, id) {
        const startTagMatch = input.match(`<div[^>]*id="${id}"[^>]*>`);
        if (!startTagMatch || startTagMatch.length === 0) return input;
        const startPos = input.indexOf(startTagMatch[0]);
        const endPos = ML2HtmlUtils.findClosingDiv(input, startPos);
        return input.substring(0, startPos) + input.substring(startPos + startTagMatch[0].length, endPos) + input.substring(endPos + '</div>'.length, input.length);
    }

    private static findClosingDiv(input, startPos) {
        let pos = startPos;
        let depth = 0;
        while (true) {
            if (input.substring(pos, pos + '<div'.length) === '<div') {
                depth++;
                pos += '<div'.length;
            } else if (input.substring(pos, pos + '</div>'.length) === '</div>') {
                depth--;
                if (depth === 0) return pos;
                pos++;
            } else {
                pos++;
            }
            if (pos >= input.length) return null;
        }
    }

    private static isIOSDevice() {
        if (navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPod/i) || navigator.userAgent.match(/iPad/i)) {
            return true;
        }
        return false;
    }

    public static fixTextCommandsFromAIPrompt(text: string): {
        text: string;
        textCommands: { [key: string]: string };
    } {
        // extract all the text commands from the text
        const textCommands = {};
        const textCommandsRegex = /\[([^\]]+)\]/g;
        let match;
        // create a map of all the text commands, with newly generated guid as the key
        // and than create span for it, and replace the text command with the span
        while ((match = textCommandsRegex.exec(text))) {
            const commandText = match[1].replace(new RegExp('\r\n|\n', 'g'), '<br/>');
            const guid = v4();
            const commandXml = this.generateProductionCommandHtml(commandText, guid);
            textCommands[guid] = `[${match[1]}]`;
            text = text.replace(`[${match[1]}]`, commandXml.outerHTML);
        }
        return {
            text,
            textCommands,
        };
    }
}
